import {
  HeadlineProps as ComponentHeadlineProps,
  ParagraphProps as ComponentParagraphProps,
  SubtitleProps as ComponentSubtitleProps,
  TextProps,
} from "./types";

/**
 * all these propsByX objects are the ones that match the actual css variables for
 * that typography. if there any updates in the typography we have to update these
 * objects to make the helper components such as Headline and Subtitle have the
 * right styles
 */

/** Headline */
/**
 * for all cases `type` and `typeface` won't be `undefined`, it will actually be a value due to
 * the default values of the props but to use the HeadlineProps type here we can't have `type`
 * and `typeface` allow `undefined`
 */
type HeadlineProps = ComponentHeadlineProps &
  Required<Pick<ComponentHeadlineProps, "type" | "typeface">>;

type PropsByHeadline = Record<HeadlineProps["type"], TextProps>;

export const propsByHeadlineMontserrat: PropsByHeadline = {
  h1: {
    fontFamily: "alpha",
    fontSize: "xxlarge",
    fontWeight: "light",
    lineHeight: "60",
    letterSpacing: "widest",
  },
  h2: {
    fontFamily: "alpha",
    fontSize: "extralarge",
    fontWeight: "light",
    lineHeight: "50",
    letterSpacing: "wide",
  },
  h3: {
    fontFamily: "alpha",
    fontSize: "large",
    fontWeight: "regular",
    lineHeight: "36",
    letterSpacing: "wide",
  },
  h4: {
    fontFamily: "alpha",
    fontSize: "mediumlarge",
    fontWeight: "regular",
    lineHeight: "36",
    letterSpacing: "wide",
  },
};

export const propsByHeadlineNunito: PropsByHeadline = {
  h1: {
    fontFamily: "numeric",
    fontSize: "51px",
    fontWeight: "light",
    lineHeight: "60",
    letterSpacing: "normal",
  },
  h2: {
    fontFamily: "numeric",
    fontSize: "26px",
    fontWeight: "semibold",
    lineHeight: "36",
    letterSpacing: "normal",
  },
  h3: {
    fontFamily: "numeric",
    fontSize: "26px",
    fontWeight: "regular",
    lineHeight: "36",
    letterSpacing: "normal",
  },
  // there's actually no h4 defined in figma so we will just
  // repeat h3 to comply with the typing
  h4: {
    fontFamily: "numeric",
    fontSize: "26px",
    fontWeight: "regular",
    lineHeight: "36",
    letterSpacing: "normal",
  },
};

/** Subtitle */
/**
 * for all cases `type` and `typeface` won't be `undefined`, it will actually be a value due to
 * the default values of the props but to use the HeadlineProps type here we can't have `type`
 * and `typeface` allow `undefined`
 */
type SubtitleProps = ComponentSubtitleProps &
  Required<Pick<ComponentSubtitleProps, "type" | "typeface">>;

type PropsBySubtitle = Record<SubtitleProps["type"], TextProps>;

export const propsBySubtitleMontserrat: PropsBySubtitle = {
  "1": {
    as: "p",
    fontFamily: "alpha",
    fontSize: "medium",
    fontWeight: "medium",
    lineHeight: "24",
    letterSpacing: "wider",
  },
  "2": {
    as: "p",
    fontFamily: "alpha",
    fontSize: "medium",
    fontWeight: "regular",
    lineHeight: "24",
    letterSpacing: "wider",
  },
  "3": {
    as: "p",
    fontFamily: "alpha",
    fontSize: "base",
    fontWeight: "medium",
    lineHeight: "24",
    letterSpacing: "normal",
  },
};

export const propsBySubtitleNunito: PropsBySubtitle = {
  "1": {
    as: "p",
    fontFamily: "numeric",
    fontSize: "17px",
    fontWeight: "semibold",
    lineHeight: "24",
    letterSpacing: "normal",
  },
  "2": {
    as: "p",
    fontFamily: "numeric",
    fontSize: "17px",
    fontWeight: "regular",
    lineHeight: "24",
    letterSpacing: "normal",
  },
  "3": {
    as: "p",
    fontFamily: "numeric",
    fontSize: "base",
    fontWeight: "semibold",
    lineHeight: "20",
    letterSpacing: "normal",
  },
};

/** Body */
/**
 * for all cases `type` and `typeface` won't be `undefined`, it will actually be a value due to
 * the default values of the props but to use the HeadlineProps type here we can't have `type`
 * and `typeface` allow `undefined`
 */
type ParagraphProps = ComponentParagraphProps &
  Required<Pick<ComponentParagraphProps, "type" | "typeface">>;
type PropsByBody = Record<ParagraphProps["type"], TextProps>;

// montserrat body styles doesnt have a type 4 so we do the
// any here for esoteric issues
export const propsByBodyMontserrat: Record<any, TextProps> = {
  "1": {
    as: "p",
    fontFamily: "alpha",
    fontSize: "base",
    fontWeight: "regular",
    lineHeight: "16",
    letterSpacing: "wider",
  },
  "2": {
    as: "p",
    fontFamily: "alpha",
    fontSize: "small",
    fontWeight: "semibold",
    lineHeight: "16",
    letterSpacing: "tighter",
  },
  "3": {
    as: "p",
    fontFamily: "alpha",
    fontSize: "small",
    fontWeight: "medium",
    lineHeight: "16",
    letterSpacing: "tighter",
  },
};

export const propsByBodyNunito: PropsByBody = {
  "1": {
    as: "p",
    fontFamily: "numeric",
    fontSize: "base",
    fontWeight: "regular",
    lineHeight: "20",
    letterSpacing: "wider",
  },
  "2": {
    as: "p",
    fontFamily: "numeric",
    fontSize: "medium",
    fontWeight: "extra-bold",
    lineHeight: "20",
    letterSpacing: "wider",
  },
  "3": {
    as: "p",
    fontFamily: "numeric",
    fontSize: "13px",
    fontWeight: "bold",
    lineHeight: "24",
    letterSpacing: "normal",
  },
  "4": {
    as: "p",
    fontFamily: "numeric",
    fontSize: "13px",
    fontWeight: "semibold",
    lineHeight: "20",
    letterSpacing: "normal",
  },
};
